import { useState } from "react";
import { Can } from "@core/permissions";
import { useSession } from "@core/session/SessionProvider";
import { Icon } from "@core/icons";
import type { PointRule } from "@core/types";
import { tokens } from "@core/theme/tokens";
import { inputStyle } from "@portal/common";
import { RuleEditorModal } from "./RuleEditorModal";
import { RuleHistoryCard } from "./RuleHistoryCard";
import { usePointRules } from "./rulesQueries";
import {
  formatRuleDate,
  ruleTableCardStyle,
  RuleEditAction,
  RuleStatusBadge,
  RuleTableHeaderRow,
  RuleTableRow,
  RuleTableStates,
} from "./ruleTableChrome";

const COLUMNS = "1.6fr 110px 150px 190px 140px 108px 132px";
const HEADERS = [
  "Acción del miembro",
  "Puntos",
  "Tope por período",
  "Vigencia",
  "Asignación",
  "Estado",
];

export function MemberRulesPanel({
  canCreate,
  canEdit,
}: {
  canCreate: boolean;
  canEdit: boolean;
}) {
  const rules = usePointRules("member");
  const { hasPermission } = useSession();
  // F1 (retry-category review_finding, 2026-09-07): "Editar" es el único
  // disparador del modal (A5, human-resolution-spec-212.json: "el toggle
  // sigue siendo el único disparador", sin botón nuevo en la tabla), y ese
  // modal es también donde vive el toggle Activo/Inactivo. Gatearlo solo
  // por `canEdit` deja a un perfil con `canInactivate` y sin `canEdit` sin
  // forma de llegar a inactivar una regla. Se ofrece con cualquiera de los
  // dos permisos; adentro del modal cada control sigue gobernado por su
  // propio permiso (RuleEditorModal.tsx).
  const canOpenEditor =
    canEdit || hasPermission("points_engine", "canInactivate");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "Todos" | "Activo" | "Inactivo"
  >("Todos");
  const [editing, setEditing] = useState<PointRule | null | "new">(null);

  const list = rules.data?.rules ?? [];
  const rows = list.filter((rule) => {
    if (
      statusFilter !== "Todos" &&
      (statusFilter === "Activo") !== rule.active
    ) {
      return false;
    }
    if (
      search.trim() &&
      !rule.action.name.toLowerCase().includes(search.trim().toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  return (
    <div>
      <div
        style={{
          ...ruleTableCardStyle,
          padding: 14,
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 220, maxWidth: 320 }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por acción"
            aria-label="Buscar por acción"
            style={{ ...inputStyle, padding: "11px 13px" }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(
              event.target.value as "Todos" | "Activo" | "Inactivo",
            )
          }
          aria-label="Estado"
          style={{
            ...inputStyle,
            width: "auto",
            minWidth: 140,
            cursor: "pointer",
          }}
        >
          {(["Todos", "Activo", "Inactivo"] as const).map((option) => (
            <option key={option} value={option}>
              {option === "Todos" ? "Estado · todos" : option}
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <Can module="points_engine" action="canCreate">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setEditing("new")}
          >
            <Icon name="plus" size={15} /> Nueva regla
          </button>
        </Can>
      </div>

      <div
        style={{ ...ruleTableCardStyle, marginBottom: 20, overflowX: "auto" }}
      >
        <div style={{ minWidth: 1120 }}>
          <RuleTableHeaderRow
            columns={COLUMNS}
            headers={HEADERS}
            trailing="Acciones"
          />
          <RuleTableStates
            isLoading={rules.isLoading}
            isError={rules.isError}
            isEmpty={!rules.isLoading && !rules.isError && rows.length === 0}
            emptyKind={list.length === 0 ? "no-data" : "no-results"}
          />
          {!rules.isLoading && !rules.isError
            ? rows.map((rule) => (
                <RuleTableRow
                  key={rule.id}
                  columns={COLUMNS}
                  active={rule.active}
                >
                  <span
                    style={{
                      font: `600 12.5px ${tokens.font.ui}`,
                      color: tokens.text.primary,
                    }}
                  >
                    {rule.action.name}
                  </span>
                  <span
                    style={{
                      font: `600 13px ${tokens.font.ui}`,
                      color: tokens.gold.light,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {rule.points}
                  </span>
                  <span
                    style={{
                      font: `400 12px ${tokens.font.ui}`,
                      color: tokens.text.secondary,
                    }}
                  >
                    {rule.capQuantity && rule.capFrequency
                      ? `${rule.capQuantity} / ${rule.capFrequency === "month" ? "mes" : "año"}`
                      : "Sin tope"}
                  </span>
                  <span
                    style={{
                      font: `400 11.5px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                    }}
                  >
                    {formatRuleDate(rule.validFrom)}
                    {rule.validUntil
                      ? ` → ${formatRuleDate(rule.validUntil)}`
                      : " · indefinida"}
                  </span>
                  <span
                    className={
                      "stat stat-" +
                      (rule.assignment === "automatic" ? "silver" : "neutral")
                    }
                  >
                    {rule.assignment === "automatic" ? "Automática" : "Manual"}
                  </span>
                  <RuleStatusBadge active={rule.active} />
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <RuleEditAction
                      visible={canOpenEditor}
                      onEdit={() => setEditing(rule)}
                    />
                  </div>
                </RuleTableRow>
              ))
            : null}
        </div>
      </div>

      <RuleHistoryCard ruleIds={list.map((rule) => rule.id)} />

      {editing !== null ? (
        <RuleEditorModal
          scope="member"
          rule={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          canCreate={canCreate}
          canEdit={canEdit}
        />
      ) : null}
    </div>
  );
}
